/**
 * v4.32 - BATCH-2026-10-31B row 56 - 2026-10-31: the approval mode shown is the one the API resolved (company mode, else the platform default); no mode is typed here any more (was 'smart_default' twice). When the API cannot resolve one, the page says so and selects nothing.
 * v4.31 - 2026-10-16 - DISCOVERY-CONFIRM-CHAIN S-9 (row 564, finding #4): after Redo Discovery the page opens the discovery chat in place (a dialog hosting DiscoveryChat) instead of navigating to '/', which never shows onboarding to a company whose first screen is the dashboard; finishing the chat closes it and reloads settings. The Redo button keeps its condition.
 * v4.28 - 2026-10-13 - G10-B (UI-PERMISSION-LIST-NOT-ENFORCED-01): role and permissions come from the shared /api/me snapshot read once in the layout; this page no longer calls /api/me
 * Settings Page - Account settings, preferences, notifications
 * v2.88 - 2026-02-06 - Added Data Proposal Approval mode selector
 * v2.77 - 2026-02-05 - Fixed fetch('/api/me') to include X-Authorization header for Clerk JWT
 * v2.38 - Fixed credentials: 'include' for SWA auth - 2026-02-01
 * v2.34 - Added Redo Discovery section - 2026-01-31
 * v2.33 - Added role-based permission checks - 2026-01-31
 * v2.27 - Full implementation with real API - 2026-01-30
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  FormControl,
  FormControlLabel,
  FormGroup,
  Switch,
  Select,
  MenuItem,
  InputLabel,
  Alert,
  CircularProgress,
  Divider,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Chip,
  Radio,
  RadioGroup
} from '@mui/material';
import {
  Save as SaveIcon,
  Business as BusinessIcon,
  Notifications as NotificationsIcon,
  Tune as PreferencesIcon,
  Warning as WarningIcon,
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
  Block as BlockIcon,
  Security as SecurityIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  Refresh as RefreshIcon,
  RateReview as RateReviewIcon
} from '@mui/icons-material';
import TopBar from '../components/TopBar';
import DiscoveryChat from '../components/DiscoveryChat';  // v4.31 - DISCOVERY-CONFIRM-CHAIN S-9
import { settingsService, discoveryResetService, approvalModeService, Settings, NotificationSettings, ApprovalModeResponse } from '../services/api';
import { usePermissions } from '../hooks/usePermissions';

// Common timezones
const TIMEZONES = [
  { value: 'America/New_York', label: 'Eastern Time (US)' },
  { value: 'America/Chicago', label: 'Central Time (US)' },
  { value: 'America/Denver', label: 'Mountain Time (US)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (US)' },
  { value: 'America/Anchorage', label: 'Alaska Time (US)' },
  { value: 'Pacific/Honolulu', label: 'Hawaii Time (US)' },
  { value: 'Europe/London', label: 'London (GMT/BST)' },
  { value: 'Europe/Paris', label: 'Paris (CET/CEST)' },
  { value: 'Europe/Berlin', label: 'Berlin (CET/CEST)' },
  { value: 'Asia/Tokyo', label: 'Tokyo (JST)' },
  { value: 'Asia/Shanghai', label: 'Shanghai (CST)' },
  { value: 'Asia/Singapore', label: 'Singapore (SGT)' },
  { value: 'Australia/Sydney', label: 'Sydney (AEST/AEDT)' },
  { value: 'UTC', label: 'UTC' }
];

const SettingsPage: React.FC = () => {
  const { t } = useTranslation();

  // v4.28 - G10-B: role and permissions of the chosen company (server answer, read once in the layout)
  const { role, hasPermission } = usePermissions();
  const canViewSettings = hasPermission('viewSettings');
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const canEditSettings = hasPermission('editSettings');

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [settings, setSettings] = useState<Settings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // v2.34 - Redo Discovery state
  const [redoDialogOpen, setRedoDialogOpen] = useState(false);
  const [redoLoading, setRedoLoading] = useState(false);
  // v4.31 - DISCOVERY-CONFIRM-CHAIN S-9: the discovery chat hosted on this page after a reset
  const [redoChatOpen, setRedoChatOpen] = useState(false);

  // Form state
  const [companyName, setCompanyName] = useState('');
  const [industry, setIndustry] = useState('');
  const [timezone, setTimezone] = useState('America/New_York');
  const [discoveryMode, setDiscoveryMode] = useState<'quick' | 'full'>('quick');
  const [notifications, setNotifications] = useState<NotificationSettings>({
    email_sync_complete: true,
    email_errors: true,
    email_weekly_summary: true,
    email_team_updates: true
  });

  // v2.88 - Approval mode state
  // v4.32 - BATCH-2026-10-31B row 56: no typed default; null until the API answers with the resolved mode
  const [approvalMode, setApprovalMode] = useState<string | null>(null);
  const [approvalModeSource, setApprovalModeSource] = useState<string | null>(null);
  const [approvalModeUnresolved, setApprovalModeUnresolved] = useState<string | null>(null);
  const [approvalModeLoadFailed, setApprovalModeLoadFailed] = useState(false);
  const [mediumRiskAutoApprove, setMediumRiskAutoApprove] = useState(false);
  const [approvalModeLoading, setApprovalModeLoading] = useState(false);
  const [approvalModeSaving, setApprovalModeSaving] = useState(false);

  // Danger zone state
  const [dangerZoneOpen, setDangerZoneOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);

  // Track if form is dirty
  const [isDirty, setIsDirty] = useState(false);


  // Load settings
  const loadSettings = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await settingsService.get();
      setSettings(response.settings);
      setCompanyName(response.settings.companyName || '');
      setIndustry(response.settings.industry || '');
      setTimezone(response.settings.timezone || 'America/New_York');
      setDiscoveryMode(response.settings.discoveryMode || 'quick');
      setNotifications(response.settings.notifications || {
        email_sync_complete: true,
        email_errors: true,
        email_weekly_summary: true,
        email_team_updates: true
      });
    } catch (err: any) {
      setError(err.userMessage || 'Failed to load settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  // v2.88 - Load approval mode
  useEffect(() => {
    const loadApprovalMode = async () => {
      try {
        setApprovalModeLoading(true);
        // v4.32 - BATCH-2026-10-31B row 56: show exactly what the API resolved (mode, source, or why there is none)
        const response = (await approvalModeService.get()) as ApprovalModeResponse & {
          approval_mode: string | null;
          approval_mode_source?: string | null;
          approval_mode_unresolved_reason?: string | null;
        };
        setApprovalMode(response.approval_mode ?? null);
        setApprovalModeSource(response.approval_mode_source ?? null);
        setApprovalModeUnresolved(response.approval_mode_unresolved_reason ?? null);
        setMediumRiskAutoApprove(response.medium_risk_auto_approve || false);
        setApprovalModeLoadFailed(false);
      } catch (err: any) {
        console.warn('v4.32: Failed to load approval mode:', err);
        setApprovalModeLoadFailed(true); // v4.32 - no fallback mode: the page says it could not load
      } finally {
        setApprovalModeLoading(false);
      }
    };
    if (canEditSettings) {
      loadApprovalMode();
    }
  }, [canEditSettings]);

  // v2.88 - Handle approval mode change
  const handleApprovalModeChange = async (newMode: string) => {
    try {
      setApprovalModeSaving(true);
      await approvalModeService.update(newMode, newMode === 'smart_default' ? mediumRiskAutoApprove : undefined);
      // v4.32 - set only after the API accepted it; the company's own mode now applies
      setApprovalMode(newMode);
      setApprovalModeSource('company');
      setApprovalModeUnresolved(null);
      setSuccess('Approval mode updated');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err?.userMessage || 'Failed to update approval mode');
    } finally {
      setApprovalModeSaving(false);
    }
  };

  // v2.88 - Handle medium risk auto-approve toggle
  const handleMediumRiskToggle = async () => {
    try {
      if (!approvalMode) return; // v4.32 - nothing to save against until a mode is chosen
      setApprovalModeSaving(true);
      const newValue = !mediumRiskAutoApprove;
      setMediumRiskAutoApprove(newValue);
      await approvalModeService.update(approvalMode, newValue);
      setSuccess('Approval mode updated');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err?.userMessage || 'Failed to update approval mode');
    } finally {
      setApprovalModeSaving(false);
    }
  };

  // Handle save
  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);
      await settingsService.update({
        companyName,
        industry,
        timezone,
        discoveryMode,
        notifications
      });
      setSuccess('Settings saved successfully');
      setIsDirty(false);
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.userMessage || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  // Handle notification toggle
  const handleNotificationChange = (key: keyof NotificationSettings) => {
    setNotifications(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
    setIsDirty(true);
  };

  // Handle delete account
  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'DELETE') {
      return;
    }

    try {
      setDeleting(true);
      await settingsService.requestDeletion();
      setDeleteDialogOpen(false);
      setSuccess('Account deletion request submitted. Please check your email to confirm.');
    } catch (err: any) {
      setError(err.userMessage || 'Failed to request account deletion');
    } finally {
      setDeleting(false);
    }
  };

  // v2.34 - Handle redo discovery
  const handleRedoDiscovery = async () => {
    try {
      setRedoLoading(true);
      setError(null);
      await discoveryResetService.reset();
      setRedoDialogOpen(false);
      setRedoLoading(false);
      // v4.31 - DISCOVERY-CONFIRM-CHAIN S-9: open the discovery chat here. navigate('/') led nowhere for a
      // company whose first screen is the dashboard (App.tsx renders /onboarding only for onboarding companies).
      setRedoChatOpen(true);
    } catch (err: any) {
      setError(err.userMessage || 'Failed to reset discovery');
      setRedoLoading(false);
    }
  };

  // v4.31 - DISCOVERY-CONFIRM-CHAIN S-9: finishing the chat closes it and reloads this page's settings.
  const handleRedoChatComplete = () => {
    setRedoChatOpen(false);
    loadSettings();
  };

  // v2.33 - Access denied for viewers
  if (!canViewSettings) {
    return (
      <Box sx={{ p: { xs: 2, sm: 3 } }}>
        <TopBar title="Settings" subtitle="Access Restricted" />
        <Container maxWidth="md" sx={{ mt: 8, textAlign: 'center' }}>
          <BlockIcon sx={{ fontSize: 64, color: '#999', mb: 2 }} />
          <Typography variant="h5" sx={{ color: '#003366', mb: 2 }}>
            Access Denied
          </Typography>
          <Typography color="text.secondary">
            You don't have permission to view settings. Contact your account owner for access.
          </Typography>
        </Container>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ p: { xs: 2, sm: 3 } }}>
        <TopBar title="Settings" subtitle="Manage your account settings" />
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <TopBar title="Settings" subtitle="Manage your account settings" />

      <Container maxWidth="md" sx={{ mt: 4 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {/* Account Information */}
        <Card sx={{ bgcolor: '#BBCDE2', mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <BusinessIcon sx={{ color: '#003366', mr: 1 }} />
              <Typography variant="h6" sx={{ color: '#003366', fontWeight: 700 }}>
                Account Information
              </Typography>
            </Box>

            <TextField
              fullWidth
              label="Company Name"
              value={companyName}
              onChange={(e) => { setCompanyName(e.target.value); setIsDirty(true); }}
              disabled={!canEditSettings}
              sx={{ mb: 3, bgcolor: 'white', borderRadius: 1 }}
            />

            <TextField
              fullWidth
              label="Industry"
              value={industry}
              onChange={(e) => { setIndustry(e.target.value); setIsDirty(true); }}
              placeholder="e.g., Construction, Retail, Healthcare"
              disabled={!canEditSettings}
              sx={{ mb: 3, bgcolor: 'white', borderRadius: 1 }}
            />

            <FormControl fullWidth sx={{ bgcolor: 'white', borderRadius: 1 }} disabled={!canEditSettings}>
              <InputLabel>Timezone</InputLabel>
              <Select
                value={timezone}
                label="Timezone"
                onChange={(e) => { setTimezone(e.target.value); setIsDirty(true); }}
              >
                {TIMEZONES.map(tz => (
                  <MenuItem key={tz.value} value={tz.value}>{tz.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </CardContent>
        </Card>

        {/* Preferences */}
        <Card sx={{ bgcolor: '#BBCDE2', mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <PreferencesIcon sx={{ color: '#003366', mr: 1 }} />
              <Typography variant="h6" sx={{ color: '#003366', fontWeight: 700 }}>
                Preferences
              </Typography>
            </Box>

            <FormControl fullWidth sx={{ bgcolor: 'white', borderRadius: 1 }} disabled={!canEditSettings}>
              <InputLabel>Discovery Mode</InputLabel>
              <Select
                value={discoveryMode}
                label="Discovery Mode"
                onChange={(e) => { setDiscoveryMode(e.target.value as 'quick' | 'full'); setIsDirty(true); }}
              >
                <MenuItem value="quick">
                  <Box>
                    <Typography>Quick Mode (~5 minutes)</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Essential questions only - best for getting started fast
                    </Typography>
                  </Box>
                </MenuItem>
                <MenuItem value="full">
                  <Box>
                    <Typography>Full Mode (~15 minutes)</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Comprehensive discovery - best for detailed insights
                    </Typography>
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>

            {/* v2.34 - Redo Discovery button */}
            {canEditSettings && (
              <Box sx={{ mt: 3 }}>
                <Divider sx={{ mb: 2 }} />
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Want to start over? You can redo the discovery process to update your business profile.
                  Your connected data sources will be preserved.
                </Typography>
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<RefreshIcon />}
                  onClick={() => setRedoDialogOpen(true)}
                >
                  Redo Discovery
                </Button>
              </Box>
            )}
          </CardContent>
        </Card>

        {/* v2.88 - Data Proposal Approval Mode */}
        {canEditSettings && (
          <Card sx={{ bgcolor: '#BBCDE2', mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <RateReviewIcon sx={{ color: '#003366', mr: 1 }} />
                <Typography variant="h6" sx={{ color: '#003366', fontWeight: 700 }}>
                  Data Proposal Approval
                </Typography>
                {approvalModeSaving && <CircularProgress size={18} sx={{ ml: 2 }} />}
              </Box>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                When the pipeline discovers your data, it generates transformation proposals.
                Choose how these proposals are handled:
              </Typography>

              {approvalModeLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : (
                <Box sx={{ bgcolor: 'white', p: 2, borderRadius: 1 }}>
                  {/* v4.32 - BATCH-2026-10-31B row 56: what the API resolved, said plainly */}
                  {approvalModeLoadFailed && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                      The approval mode could not be loaded. Reload the page to try again.
                    </Alert>
                  )}
                  {!approvalModeLoadFailed && approvalModeUnresolved && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      No approval mode is set for your company and no platform default is available. Choose a mode below; until then the data setup cannot pass the approval step.
                    </Alert>
                  )}
                  {!approvalModeLoadFailed && approvalModeSource === 'app_config' && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                      Your company has not chosen a mode, so the platform default shown below applies.
                    </Typography>
                  )}
                  <RadioGroup
                    value={approvalMode ?? ''}
                    onChange={(e) => handleApprovalModeChange(e.target.value)}
                  >
                    <FormControlLabel
                      value="auto"
                      control={<Radio sx={{ '&.Mui-checked': { color: '#008080' } }} />}
                      label={
                        <Box>
                          <Typography>Auto-Approve All</Typography>
                          <Typography variant="caption" color="text.secondary">
                            All proposals are automatically approved. Fastest setup, no review needed.
                          </Typography>
                        </Box>
                      }
                      sx={{ mb: 1 }}
                    />
                    <Divider sx={{ my: 1 }} />
                    <FormControlLabel
                      value="smart_default"
                      control={<Radio sx={{ '&.Mui-checked': { color: '#008080' } }} />}
                      label={
                        <Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography>Smart Default</Typography>
                            <Chip label="Recommended" size="small" color="primary" sx={{ height: 20, fontSize: '0.65rem' }} />
                          </Box>
                          <Typography variant="caption" color="text.secondary">
                            Low-risk proposals auto-approved. High-risk proposals require your review.
                          </Typography>
                        </Box>
                      }
                      sx={{ mb: 1 }}
                    />
                    {approvalMode === 'smart_default' && (
                      <Box sx={{ pl: 4, mb: 1 }}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={mediumRiskAutoApprove}
                              onChange={handleMediumRiskToggle}
                              sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#008080' } }}
                            />
                          }
                          label={
                            <Typography variant="body2">
                              Also auto-approve medium-risk proposals
                            </Typography>
                          }
                        />
                      </Box>
                    )}
                    <Divider sx={{ my: 1 }} />
                    <FormControlLabel
                      value="full_review"
                      control={<Radio sx={{ '&.Mui-checked': { color: '#008080' } }} />}
                      label={
                        <Box>
                          <Typography>Full Review</Typography>
                          <Typography variant="caption" color="text.secondary">
                            All proposals require manual review. Most control, takes longer.
                          </Typography>
                        </Box>
                      }
                    />
                  </RadioGroup>
                </Box>
              )}
            </CardContent>
          </Card>
        )}

        {/* v2.33 - Permissions Section */}
        <Card sx={{ bgcolor: '#BBCDE2', mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <SecurityIcon sx={{ color: '#003366', mr: 1 }} />
              <Typography variant="h6" sx={{ color: '#003366', fontWeight: 700 }}>
                Your Permissions
              </Typography>
            </Box>

            <Box sx={{ bgcolor: 'white', p: 2, borderRadius: 1, mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#003366', mr: 2 }}>
                  Your Role:
                </Typography>
                <Chip
                  label={t(`teamAccess.roles.${role}`, { defaultValue: String(role || '') })}
                  color={role === 'owner' ? 'primary' : role === 'admin' ? 'secondary' : 'default'}
                  size="small"
                />
              </Box>
              <Typography variant="body2" color="text.secondary">
                {t(`teamAccess.roleDescriptions.${role}`, { defaultValue: '' })}
              </Typography>
            </Box>

            <Typography variant="subtitle2" sx={{ color: '#003366', mb: 2 }}>
              What you can do:
            </Typography>

            <Box sx={{ bgcolor: 'white', p: 2, borderRadius: 1 }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  {hasPermission('viewDashboard') ? (
                    <CheckIcon sx={{ color: '#008080', mr: 1, fontSize: 18 }} />
                  ) : (
                    <CancelIcon sx={{ color: '#999', mr: 1, fontSize: 18 }} />
                  )}
                  <Typography variant="body2">View dashboards</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  {hasPermission('createQueries') ? (
                    <CheckIcon sx={{ color: '#008080', mr: 1, fontSize: 18 }} />
                  ) : (
                    <CancelIcon sx={{ color: '#999', mr: 1, fontSize: 18 }} />
                  )}
                  <Typography variant="body2">Create queries</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  {hasPermission('manageConnections') ? (
                    <CheckIcon sx={{ color: '#008080', mr: 1, fontSize: 18 }} />
                  ) : (
                    <CancelIcon sx={{ color: '#999', mr: 1, fontSize: 18 }} />
                  )}
                  <Typography variant="body2">Manage connections</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  {hasPermission('inviteMembers') ? (
                    <CheckIcon sx={{ color: '#008080', mr: 1, fontSize: 18 }} />
                  ) : (
                    <CancelIcon sx={{ color: '#999', mr: 1, fontSize: 18 }} />
                  )}
                  <Typography variant="body2">Invite members</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  {hasPermission('editSettings') ? (
                    <CheckIcon sx={{ color: '#008080', mr: 1, fontSize: 18 }} />
                  ) : (
                    <CancelIcon sx={{ color: '#999', mr: 1, fontSize: 18 }} />
                  )}
                  <Typography variant="body2">Edit settings</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  {hasPermission('viewBilling') ? (
                    <CheckIcon sx={{ color: '#008080', mr: 1, fontSize: 18 }} />
                  ) : (
                    <CancelIcon sx={{ color: '#999', mr: 1, fontSize: 18 }} />
                  )}
                  <Typography variant="body2">View billing</Typography>
                </Box>
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Notifications */}
        <Card sx={{ bgcolor: '#BBCDE2', mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <NotificationsIcon sx={{ color: '#003366', mr: 1 }} />
              <Typography variant="h6" sx={{ color: '#003366', fontWeight: 700 }}>
                Email Notifications
              </Typography>
            </Box>

            <Box sx={{ bgcolor: 'white', p: 2, borderRadius: 1 }}>
              <FormGroup>
                <FormControlLabel
                  control={
                    <Switch
                      checked={notifications.email_sync_complete}
                      onChange={() => handleNotificationChange('email_sync_complete')}
                      disabled={!canEditSettings}
                      sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#008080' } }}
                    />
                  }
                  label={
                    <Box>
                      <Typography>Sync Complete</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Get notified when a data source sync completes
                      </Typography>
                    </Box>
                  }
                  sx={{ mb: 2 }}
                />

                <Divider sx={{ my: 1 }} />

                <FormControlLabel
                  control={
                    <Switch
                      checked={notifications.email_errors}
                      onChange={() => handleNotificationChange('email_errors')}
                      disabled={!canEditSettings}
                      sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#008080' } }}
                    />
                  }
                  label={
                    <Box>
                      <Typography>Errors & Alerts</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Get notified about sync errors and important alerts
                      </Typography>
                    </Box>
                  }
                  sx={{ mb: 2 }}
                />

                <Divider sx={{ my: 1 }} />

                <FormControlLabel
                  control={
                    <Switch
                      checked={notifications.email_weekly_summary}
                      onChange={() => handleNotificationChange('email_weekly_summary')}
                      disabled={!canEditSettings}
                      sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#008080' } }}
                    />
                  }
                  label={
                    <Box>
                      <Typography>Weekly Summary</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Receive a weekly summary of your data insights
                      </Typography>
                    </Box>
                  }
                  sx={{ mb: 2 }}
                />

                <Divider sx={{ my: 1 }} />

                <FormControlLabel
                  control={
                    <Switch
                      checked={notifications.email_team_updates}
                      onChange={() => handleNotificationChange('email_team_updates')}
                      disabled={!canEditSettings}
                      sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#008080' } }}
                    />
                  }
                  label={
                    <Box>
                      <Typography>Team Updates</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Get notified when team members join or roles change
                      </Typography>
                    </Box>
                  }
                />
              </FormGroup>
            </Box>
          </CardContent>
        </Card>

        {/* Save Button - v2.33: Only show if user can edit */}
        {canEditSettings && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 4 }}>
            <Button
              variant="contained"
              size="large"
              onClick={handleSave}
              disabled={saving || !isDirty}
              startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
              sx={{
                bgcolor: '#008080',
                '&:hover': { bgcolor: '#006666' },
                minWidth: 150
              }}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </Box>
        )}

        {/* Danger Zone - v2.33: Only show for owners */}
        {hasPermission('deleteAccount') && (
        <Card sx={{ bgcolor: '#fff5f5', border: '1px solid #dc3545' }}>
          <CardContent>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                cursor: 'pointer'
              }}
              onClick={() => setDangerZoneOpen(!dangerZoneOpen)}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <WarningIcon sx={{ color: '#dc3545', mr: 1 }} />
                <Typography variant="h6" sx={{ color: '#dc3545', fontWeight: 700 }}>
                  Danger Zone
                </Typography>
              </Box>
              {dangerZoneOpen ? <CollapseIcon /> : <ExpandIcon />}
            </Box>

            <Collapse in={dangerZoneOpen}>
              <Box sx={{ mt: 3 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Once you delete your account, there is no going back. All your data, connections,
                  team members, and settings will be permanently deleted.
                </Typography>

                <Button
                  variant="outlined"
                  color="error"
                  onClick={() => setDeleteDialogOpen(true)}
                >
                  Delete Account
                </Button>
              </Box>
            </Collapse>
          </CardContent>
        </Card>
        )}
      </Container>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => !deleting && setDeleteDialogOpen(false)}
      >
        <DialogTitle sx={{ color: '#dc3545' }}>
          Delete Account
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            This action is <strong>permanent and cannot be undone</strong>. All your data,
            connections, team members, and settings will be deleted.
          </DialogContentText>
          <DialogContentText sx={{ mt: 2, mb: 2 }}>
            To confirm, type <strong>DELETE</strong> below:
          </DialogContentText>
          <TextField
            fullWidth
            value={deleteConfirmText}
            onChange={(e) => setDeleteConfirmText(e.target.value.toUpperCase())}
            placeholder="Type DELETE to confirm"
            disabled={deleting}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteAccount}
            disabled={deleteConfirmText !== 'DELETE' || deleting}
          >
            {deleting ? 'Deleting...' : 'Delete My Account'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* v2.34 - Redo Discovery Confirmation Dialog */}
      <Dialog
        open={redoDialogOpen}
        onClose={() => !redoLoading && setRedoDialogOpen(false)}
      >
        <DialogTitle>
          Redo Discovery?
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will reset your business profile and take you through the discovery process again.
          </DialogContentText>
          <DialogContentText sx={{ mt: 2 }}>
            <strong>Your connected data sources will be preserved.</strong> Only your discovery
            answers will be cleared.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRedoDialogOpen(false)} disabled={redoLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleRedoDiscovery}
            disabled={redoLoading}
            startIcon={redoLoading ? <CircularProgress size={20} /> : <RefreshIcon />}
          >
            {redoLoading ? 'Resetting...' : 'Start Over'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* v4.31 - DISCOVERY-CONFIRM-CHAIN S-9: the discovery chat, opened in place after a reset */}
      <Dialog open={redoChatOpen} fullWidth maxWidth="md">
        <DialogContent sx={{ height: '80vh', display: 'flex', flexDirection: 'column' }}>
          <DiscoveryChat onComplete={handleRedoChatComplete} />
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default SettingsPage;
